import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { BottomSheetPopup, BottomSheetPopupFooter } from '../components/common/BottomSheetPopup';
import { Clickable2 } from '../components/common/Clickable2';
import { MainLoader } from '../components/load-main/MainLoader';
import { CmsPagesApiHtmlScroll } from '../components/common/CmsPagesApiHtmlScroll';
import { cmsPageHtmlToDisplayText } from '../shared/utils/htmlToPlainText';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import {
  fetchCmsPages,
  findCmsPageByTitleOrMetaTitle,
  resolveCmsPageLocalizedHtml,
  resolveCmsPageLocalizedTitle,
} from '../data/domains/config';
import { useTranslation } from '../shared/i18n';
import { pillButtonLabelCentered } from '../shared/theme/sharedStyles';
import { DASH_STUDIO_RADIUS } from '../shared/theme/studio/dashboardConstants';

function createRegisterTermsModalStyles(c: ThemeColors) {
  return StyleSheet.create({
    bodyScroll: {
      flex: 1,
      minHeight: 0,
    },
    bodyScrollContent: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.md,
    },
    cmsBodyText: {
      fontSize: 14,
      lineHeight: 22,
      color: c.textSecondary,
    },
    loadingWrap: {
      flex: 1,
      minHeight: 160,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xl,
    },
    fallbackText: {
      ...appTheme.typography.bodySmall,
      lineHeight: 22,
      color: c.textSecondary,
    },
    acceptButton: {
      backgroundColor: c.primary,
      paddingVertical: appTheme.spacing.md,
      borderRadius: DASH_STUDIO_RADIUS,
      alignItems: 'center',
      justifyContent: 'center',
    },
    acceptButtonText: {
      ...pillButtonLabelCentered,
      fontSize: 16,
      fontWeight: '600',
      color: c.onPrimary,
    },
    footerBtnOuter: { flex: 1 },
    sheetColumn: {
      flex: 1,
      minHeight: 0,
      width: '100%',
    },
  });
}

export type RegisterTermsModalProps = {
  visible: boolean;
  onClose: () => void;
  onAccept?: () => void;
};

/**
 * Terms sheet for registration flows inside `AuthSheetHost`.
 * Stack navigation to `TermsAndConditions` opens under the auth overlay; this modal renders above it.
 * Looks up CMS page by English title / meta_title=`terms`; shows `a_title` / `a_content` when language is Arabic.
 */
export function RegisterTermsModal({ visible, onClose, onAccept }: RegisterTermsModalProps) {
  const { t, locale } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createRegisterTermsModalStyles(colors), [colors]);
  const app_language = locale === 'ar' ? 'ar' : 'en';
  const [content, setContent] = useState('');
  const [page_title, setPageTitle] = useState('');
  const [is_loading, setIsLoading] = useState(false);

  const loadTerms = useCallback(async () => {
    setIsLoading(true);
    try {
      const pages = await fetchCmsPages();
      const tc_page = findCmsPageByTitleOrMetaTitle(pages, 'Terms & Conditions', 'terms');
      if (tc_page) {
        setContent(resolveCmsPageLocalizedHtml(tc_page, app_language));
        setPageTitle(resolveCmsPageLocalizedTitle(tc_page, app_language));
      } else {
        setContent('');
        setPageTitle('');
      }
    } catch {
      setContent('');
      setPageTitle('');
    } finally {
      setIsLoading(false);
    }
  }, [app_language]);

  useEffect(() => {
    if (visible) {
      void loadTerms();
    }
  }, [visible, loadTerms]);

  const handleAccept = useCallback(() => {
    onAccept?.();
    onClose();
  }, [onAccept, onClose]);

  const cms_text = cmsPageHtmlToDisplayText(content);
  const sheet_title = page_title || t('settings.termsAndConditions');

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={onClose}
      title={sheet_title}
      animationType="slide"
      showDragHandle
      sheetHeightMode="fill">
      <View style={styles.sheetColumn}>
        {is_loading ? (
          <View style={styles.loadingWrap}>
            <MainLoader size={40} color={colors.primary} />
          </View>
        ) : cms_text ? (
          <CmsPagesApiHtmlScroll
            html={content}
            scrollViewStyle={styles.bodyScroll}
            contentContainerStyle={styles.bodyScrollContent}
            textStyle={styles.cmsBodyText}
          />
        ) : (
          <ScrollView style={styles.bodyScroll} contentContainerStyle={styles.bodyScrollContent} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator>
            <Text style={styles.fallbackText}>{t('auth.agreeToTerms')}</Text>
          </ScrollView>
        )}

        {onAccept ? (
          <BottomSheetPopupFooter>
            <Clickable2
              style={styles.footerBtnOuter}
              onPress={handleAccept}
              haptics
              accessibilityRole="button"
              accessibilityLabel={t('terms.acceptButton')}>
              <View style={styles.acceptButton}>
                <Text style={styles.acceptButtonText}>{t('terms.acceptButton')}</Text>
              </View>
            </Clickable2>
          </BottomSheetPopupFooter>
        ) : null}
      </View>
    </BottomSheetPopup>
  );
}

export default RegisterTermsModal;
